var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton, BCard, BTab, BTabs } from "bootstrap-vue";
import { computed, onMounted, ref, watch } from "vue";
import { exportHistoryToFileSource, fetchHistoryExportRecords, reimportHistoryFromRecord, } from "@/api/histories.export";
import { areEqual } from "@/components/Common/models/exportRecordModel";
import { useConfirmDialog } from "@/composables/confirmDialog";
import { useFileSources } from "@/composables/fileSources";
import { DEFAULT_EXPORT_PARAMS, useShortTermStorage } from "@/composables/shortTermStorage";
import { useTaskMonitor } from "@/composables/taskMonitor";
import { useHistoryStore } from "@/stores/historyStore";
import { copy as sendToClipboard } from "@/utils/clipboard";
import { absPath } from "@/utils/redirect";
import { errorMessageAsString } from "@/utils/simple-error";
import ExportOptions from "./ExportOptions.vue";
import ExportToFileSourceForm from "@/components/Common/ExportForm.vue";
import ExportToRDMRepositoryForm from "@/components/Common/ExportRDMForm.vue";
import ExportRecordDetails from "@/components/Common/ExportRecordDetails.vue";
import ExportRecordTable from "@/components/Common/ExportRecordTable.vue";
import RDMCredentialsInfo from "@/components/Common/RDMCredentialsInfo.vue";
import ExternalLink from "@/components/ExternalLink.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoryExport',
    props: {
        historyId: null
    },
    setup(__props) {
        const props = __props;
        const { isRunning: isExportTaskRunning, waitForTask, requestHasFailed: taskMonitorRequestFailed, hasFailed: taskHasFailed, } = useTaskMonitor();
        const { hasWritable: hasWritableFileSources } = useFileSources({ exclude: ["rdm"] });
        const { hasWritable: hasWritableRDMFileSources, getFileSourceById } = useFileSources({ include: ["rdm"] });
        const { isPreparing: isPreparingDownload, prepareHistoryDownload, downloadObjectByRequestId, getDownloadObjectUrl, } = useShortTermStorage();
        const { confirm } = useConfirmDialog();
        library.add(faFileExport);
        const POLLING_DELAY = 3000;
        const exportParams = ref(DEFAULT_EXPORT_PARAMS);
        const isLoadingRecords = ref(true);
        const exportRecords = ref([]);
        const history = ref();
        const isLoadingHistory = ref(true);
        const historyName = computed(() => { var _a, _b; return (_b = (_a = history.value) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : props.historyId; });
        const defaultFileName = computed(() => `(Galaxy History) ${historyName.value}`);
        const latestExportRecord = computed(() => { var _a; return (((_a = exportRecords.value) === null || _a === void 0 ? void 0 : _a.length) ? exportRecords.value.at(0) : null); });
        const isLatestExportRecordReadyToDownload = computed(() => latestExportRecord.value &&
            latestExportRecord.value.isUpToDate &&
            latestExportRecord.value.canDownload &&
            areEqual(latestExportRecord.value.exportParams, exportParams.value));
        const canGenerateDownload = computed(() => !isPreparingDownload.value && !isLatestExportRecordReadyToDownload.value);
        const previousExportRecords = computed(() => (exportRecords.value ? exportRecords.value.slice(1) : []));
        const hasPreviousExports = computed(() => { var _a; return ((_a = previousExportRecords.value) === null || _a === void 0 ? void 0 : _a.length) > 0; });
        const availableRecordsMessage = computed(() => isLoadingRecords.value
            ? "Loading export records..."
            : "This history has no export records yet. You can choose one of the export options above.");
        const historyStore = useHistoryStore();
        const isFatalError = ref(false);
        const errorMessage = ref();
        const actionMessage = ref();
        const actionMessageVariant = ref();
        const zenodoSource = computed(() => getFileSourceById("zenodo"));
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            if (yield loadHistory()) {
                updateExports();
            }
        }));
        watch(isExportTaskRunning, (newValue, oldValue) => {
            const hasFinished = oldValue && !newValue;
            if (hasFinished) {
                updateExports();
            }
        });
        function loadHistory() {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                isLoadingHistory.value = true;
                try {
                    history.value =
                        (_a = historyStore.getHistoryById(props.historyId, false)) !== null && _a !== void 0 ? _a : (yield historyStore.loadHistoryById(props.historyId));
                    return true;
                }
                catch (error) {
                    errorMessage.value = errorMessageAsString(error);
                    isFatalError.value = true;
                    return false;
                }
                finally {
                    isLoadingHistory.value = false;
                }
            });
        }
        function updateExports() {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                isLoadingRecords.value = true;
                try {
                    errorMessage.value = undefined;
                    exportRecords.value = yield fetchHistoryExportRecords(props.historyId);
                    const shouldWaitForTask = ((_a = latestExportRecord.value) === null || _a === void 0 ? void 0 : _a.isPreparing) &&
                        !isExportTaskRunning.value &&
                        !taskMonitorRequestFailed.value &&
                        !taskHasFailed.value;
                    if (shouldWaitForTask) {
                        waitForTask(latestExportRecord.value.taskUUID, POLLING_DELAY);
                    }
                    if (taskMonitorRequestFailed.value) {
                        errorMessage.value = "Something went wrong trying to get the export progress. Please check back later.";
                    }
                    if (taskHasFailed.value) {
                        errorMessage.value = "Something went wrong trying to export the history. Please try again later.";
                    }
                }
                catch (error) {
                    errorMessage.value = errorMessageAsString(error);
                }
                finally {
                    isLoadingRecords.value = false;
                }
            });
        }
        function doExportToFileSource(exportDirectory, fileName) {
            return __awaiter(this, void 0, void 0, function* () {
                yield exportHistoryToFileSource(props.historyId, exportDirectory, fileName, exportParams.value);
                updateExports();
            });
        }
        function prepareDownload() {
            return __awaiter(this, void 0, void 0, function* () {
                yield prepareHistoryDownload(props.historyId, { pollDelayInMs: POLLING_DELAY, exportParams: exportParams.value });
                updateExports();
            });
        }
        function downloadFromRecord(record) {
            if (record.canDownload) {
                downloadObjectByRequestId(record.stsDownloadId);
            }
        }
        function copyDownloadLinkFromRecord(record) {
            if (record.canDownload) {
                const relativeLink = getDownloadObjectUrl(record.stsDownloadId);
                sendToClipboard(absPath(relativeLink), "Download link copied to your clipboard");
            }
        }
        function reimportFromRecord(record) {
            return __awaiter(this, void 0, void 0, function* () {
                const confirmed = yield confirm(`Do you really want to import a new copy of this history exported ${record.elapsedTime}?`);
                if (confirmed) {
                    try {
                        yield reimportHistoryFromRecord(record);
                        actionMessageVariant.value = "info";
                        actionMessage.value =
                            "The history is being imported in the background. Check your histories after a while to find it.";
                    }
                    catch (error) {
                        actionMessageVariant.value = "danger";
                        actionMessage.value = errorMessageAsString(error);
                    }
                }
            });
        }
        function onActionMessageDismissedFromRecord() {
            actionMessage.value = undefined;
            actionMessageVariant.value = undefined;
        }
        function updateExportParams(newParams) {
            exportParams.value = Object.assign({}, newParams);
        }
        return { __sfc: true, isExportTaskRunning, waitForTask, taskMonitorRequestFailed, taskHasFailed, hasWritableFileSources, hasWritableRDMFileSources, getFileSourceById, isPreparingDownload, prepareHistoryDownload, downloadObjectByRequestId, getDownloadObjectUrl, confirm, props, POLLING_DELAY, exportParams, isLoadingRecords, exportRecords, history, isLoadingHistory, historyName, defaultFileName, latestExportRecord, isLatestExportRecordReadyToDownload, canGenerateDownload, previousExportRecords, hasPreviousExports, availableRecordsMessage, historyStore, isFatalError, errorMessage, actionMessage, actionMessageVariant, zenodoSource, loadHistory, updateExports, doExportToFileSource, prepareDownload, downloadFromRecord, copyDownloadLinkFromRecord, reimportFromRecord, onActionMessageDismissedFromRecord, updateExportParams, FontAwesomeIcon, BAlert, BButton, BCard, BTab, BTabs, ExportOptions, ExportToFileSourceForm, ExportToRDMRepositoryForm, ExportRecordDetails, ExportRecordTable, RDMCredentialsInfo, ExternalLink, LoadingSpan };
    }
});
